import { Theme } from "@mui/material";
import dayjs, { Dayjs } from "@/lib/dayjs";

export {
  PLACEHOLDER_IMAGE,
  getBucketUrl,
  getImageUrl,
  getCoverImageUrl,
  resolveImageUrls,
} from "./imageUtils";

export type { GetImageUrlOptions } from "./imageUtils";

export const formatMoney = (amount?: number) => `$${(amount ?? 0).toFixed(2)}`;

export const formatIssue = (issue: string) => {
  if (!issue) return "";
  return issue
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getDateLabel = (dateString: string) => {
  const date = dayjs(dateString);
  if (date.isSame(dayjs(), "day")) return "Today";
  if (date.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("DD MMM, YYYY");
};

export const formatCountryCode = (code?: string): string => {
  if (!code) return "+1";
  return code.startsWith("+") ? code : `+${code}`;
};

export const formatDate = (dateString: string | number | Date) => {
  if (!dateString) return "-";
  return dayjs(dateString).tz().format("DD MMM, YYYY");
};

export const formatDateTime = (dateString: string | number | Date) => {
  if (!dateString) return "-";
  return dayjs(dateString).tz().format("DD MMM, YYYY, hh:mm A");
};

export const toChicagoUTC = (date?: string | number | Date | dayjs.Dayjs, mode?: "start" | "end") => {
  let d = dayjs.tz(date);
  if (mode === "start") d = d.startOf("day");
  if (mode === "end") d = d.endOf("day");
  return d.utc().format();
};

// Accepts "14:30", "02:30 PM" or an ISO string and returns that clock time on today's Chicago date.
export const parseTimeValue = (value?: string): Dayjs | null => {
  if (!value) return null;

  const atClock = (hour: number, minute: number) =>
    dayjs.tz().hour(hour).minute(minute).second(0).millisecond(0);

  const trimmed = value.trim();
  const twelveHour = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (twelveHour) {
    const hour = (Number(twelveHour[1]) % 12) + (twelveHour[3].toUpperCase() === "PM" ? 12 : 0);
    return atClock(hour, Number(twelveHour[2]));
  }

  const twentyFourHour = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHour) return atClock(Number(twentyFourHour[1]), Number(twentyFourHour[2]));

  const parsed = dayjs(trimmed).tz();
  return parsed.isValid() ? atClock(parsed.hour(), parsed.minute()) : null;
};

export const timeToMinutes = (time?: Dayjs | null) =>
  time?.isValid() ? time.hour() * 60 + time.minute() : null;

export const isSameDay = (a?: string | Date | Dayjs | null, b?: string | Date | Dayjs | null) =>
  !!a && !!b && dayjs(a).isSame(dayjs(b), "day");

export const formatAddress = (addr?: string | { houseNo?: string, area?: string, landmark?: string, city?: string, state?: string, pinCode?: string | number, country?: string }) => {
  if (!addr) return "-";
  if (typeof addr === "string") return addr;
  const { houseNo, area, landmark, city, state, pinCode, country } = addr;
  return [houseNo, area, landmark, city, state, pinCode, country].filter(Boolean).join(", ") || "-";
};

export const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 KB";

  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;

  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;

  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};


export const DESCRIPTION_MAX_LENGTH = 500;

export const publicPaths = ["/login", "/signup", "/design-guide", "/session-expired", "/forgot-password", "/change-password", "/otp-verify", "/policy", "/delete_account_request"];
export const Regex = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
  decimal: /^\d*\.?\d{0,2}$/,
  integer: /^\d*$/,
  onlyNumber: /^[0-9]$/,
  phone: /^\d{10}$/,
};


export const renderList = (list?: (string | { name: string })[]) =>
  Array.isArray(list) && list.length > 0
    ? list.map(item => typeof item === "string" ? item : item.name).join(", ")
    : "-";

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  path: string;
  role?: string[];
  expanded?: boolean;
  badge?: number;
  subMenu?: MenuItem[];
}

export const convertFieldNameToLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};


export const appliesToOptions = [
  "All Orders",
  // "Order Total",
  // "Box",
  "New Users",
  "Existing Users",
  // "Subscribed Users",
  // "One-Time Orders",
  // "Flash Sale Discount",
  // "Other",
];

export const discountTypes = [
  "FLAT",
  "PERCENTAGE",
  // "Cart Value Discount",
  // "First Order Discount",
  // "Premium Member Discount",
  // "Flash Sale Discount",
  // "Other"
];

const COUPON_CODE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

export const generateCouponCode = (length = 8): string => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => COUPON_CODE_CHARS[byte % COUPON_CODE_CHARS.length]).join("");
};

export const userGroupOptions = [
  { name: "New Users", _id: "NEW" },
  { name: "Existing Users", _id: "EXISTING" },
  // { name: "Premium Users", _id: "PREMIUM" },
];


export const menuItems: MenuItem[] = [
  // {
  //   "id": "dashboard",
  //   "label": "Dashboard",
  //   "icon": "Dashboard.svg",
  //   "path": "/dashboard",
  //   "role": ["ADMIN", "KITCHEN"],
  // },
  {
    "id": "orders",
    "label": "Orders",
    "icon": "Orders.svg",
    "path": "/orders",
    "role": ["ADMIN", "KITCHEN", "AGENCY"]
  },
  {
    "id": "menu",
    "label": "Menu",
    "icon": "Menu.svg",
    "path": "/menu",
    "expanded": true,
    "role": ["ADMIN", "KITCHEN"],
    "subMenu": [
      {
        "id": "categories",
        "label": "Categories",
        "path": "/menu/categories",
        "role": ["ADMIN"],
      },
      {
        "id": "dishes",
        "label": "Dishes",
        "path": "/menu/dishes",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "sides",
        "label": "Sides",
        "path": "/menu/sides",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "drinks",
        "label": "Drinks",
        "path": "/menu/drinks",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "schedule_menu",
        "label": "Schedule Menu",
        "path": "/menu/schedule-menu",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "holiday",
        "label": "Holiday",
        "path": "/menu/holiday",
        "role": ["ADMIN"]
      },
    ]
  },
  {
    "id": "customers",
    "label": "Customers",
    "icon": "Customers.svg",
    "path": "/customers",
    "role": ["ADMIN"]
  },
  {
    "id": "preference_dietary",
    "label": "Preference & Allergy",
    "icon": "Preference_And_Dietary.svg",
    "path": "/preference-dietary",
    "role": ["ADMIN"]
  },
  {
    "id": "kitchens",
    "label": "Kitchens",
    "icon": "Kitchens.svg",
    "path": "/kitchens",
    "badge": 6,
    "role": ["ADMIN"]
  },
  {
    "id": "Delivery",
    "label": "Delivery",
    "icon": "Menu.svg",
    "path": "/delivery",
    "role": ["ADMIN", "AGENCY"],
    "expanded": true,
    "subMenu": [
      {
        "id": "Agencies",
        "label": "Agencies",
        "path": "/delivery/agencies",
        "role": ["ADMIN"]
      },
      {
        "id": "drivers",
        "label": "Drivers",
        "path": "/delivery/drivers",
        "role": ["AGENCY", "ADMIN"]
      },
    ]
  },
  {
    "id": "rewards",
    "label": "Rewards",
    "icon": "Rewards.svg",
    "path": "/offers",
    "expanded": true,
    "role": ["ADMIN"],
    "subMenu": [
      {
        "id": "offers",
        "label": "Offers",
        "path": "/offers",
        "role": ["ADMIN"]
      },
      {
        "id": "discount_codes",
        "label": "Discount Codes",
        "path": "/discount-codes",
        "role": ["ADMIN"]
      },
      {
        "id": "vacation_offers",
        "label": "Comeback Offers",
        "path": "/vacation/offers",
        "role": ["ADMIN"]
      },
      {
        "id": "vacation_users",
        "label": "Users on Vacation",
        "path": "/vacation/users",
        "role": ["ADMIN"]
      },
    ]
  },
  {
    "id": "settings",
    "label": "Settings",
    "icon": "Settings.svg",
    "path": "/settings",
    "expanded": true,
    "role": ["ADMIN"],
    "subMenu": [
      // {
      //   "id": "general",
      //   "label": "General",
      //   "path": "/settings/general",
      //   "role": ["ADMIN"]
      // },
      {
        "id": "policies",
        "label": "Policies",
        "path": "/settings/policies",
        "role": ["ADMIN"]
      },
      {
        "id": "mail_templates",
        "label": "Mail Templates",
        "path": "/settings/mail-templates",
        "role": ["ADMIN"]
      },
      {
        "id": "notification_templates",
        "label": "Notification Templates",
        "path": "/settings/notification-templates",
        "role": ["ADMIN"]
      },
      {
        "id": "notification_manager",
        "label": "System Manager",
        "path": "/settings/notification-manager",
        "role": ["ADMIN"]
      },
      {
        "id": "imp_links",
        "label": "Document Links",
        "path": "/settings/imp-links",
        "role": ["ADMIN"]
      },
      {
        "id": "app_config",
        "label": "App Config",
        "path": "/settings/app-config",
        "role": ["ADMIN"]
      },
      {
        "id": "replacement_restrict",
        "label": "Replacement Restrict",
        "path": "/settings/replacement-restrict",
        "role": ["ADMIN"]
      },
      {
        "id": "time_management",
        "label": "Time Management",
        "path": "/settings/time-management",
        "role": ["ADMIN"]
      },
      // {
      //   "id": "users_roles",
      //   "label": "Users & Roles",
      //   "path": "/settings/users-roles",
      //   "role": ["ADMIN"]
      // }
    ]
  },
  {
    "id": "supports",
    "label": "Supports",
    "icon": "Supports.svg",
    "path": "/supports",
    "expanded": true,
    "role": ["ADMIN"],
    "subMenu": [
      {
        "id": "general_tickets",
        "label": "General Tickets",
        "path": "/supports/general",
        "role": ["ADMIN"]
      },
      {
        "id": "refund_tickets",
        "label": "Refund Tickets",
        "path": "/supports/refund",
        "role": ["ADMIN"]
      },
      {
        "id": "kitchen_tickets",
        "label": "Kitchen Tickets",
        "path": "/supports/kitchen",
        "role": ["ADMIN"]
      },
      {
        "id": "agency_tickets",
        "label": "Agency Tickets",
        "path": "/supports/agency",
        "role": ["ADMIN"]
      },
    ]
  },
  {
    "id": "vender_supports",
    "label": "Supports",
    "icon": "Supports.svg",
    "path": "/vender-supports",
    "role": ["KITCHEN", "AGENCY"]
  },
  // {
  //   "id": "help",
  //   "label": "Help",
  //   "icon": "Help.svg",
  //   "path": "/help",
  //   "role": ["ADMIN"]
  // }
];

export const getFilteredMenuItems = (userRole: string): MenuItem[] => {
  const hasAccess = (roles?: string[]) => !roles || roles.includes(userRole);
  return menuItems.map(item => {
    if (!hasAccess(item.role)) return null;
    const subMenu = item.subMenu?.filter(sub => hasAccess(sub.role));
    if (item.subMenu && !subMenu?.length) return null;
    return { ...item, subMenu };
  }).filter(Boolean) as MenuItem[];
};

export const getFirstMenuPath = (userRole: string): string => {
  const filtered = getFilteredMenuItems(userRole);
  if (filtered.length > 0) {
    if (filtered[0].subMenu && filtered[0].subMenu.length > 0) {
      return filtered[0].subMenu[0].path;
    }
    return filtered[0].path;
  }
  return "/login";
};

export const isPathAllowed = (path: string, userRole: string): boolean => {
  const purePath = path.split("?")[0];
  if (publicPaths.some(p => purePath.startsWith(p))) return true;
  if (purePath === "/" || purePath === "/access-denied") return true;
  if (purePath === "/edit-profile") return userRole === "KITCHEN";

  const findPath = (items: MenuItem[]): MenuItem | undefined => {
    for (const item of items) {
      if (item.subMenu) {
        const sub = findPath(item.subMenu);
        if (sub) return sub;
      }
      if (purePath === item.path || purePath.startsWith(item.path + "/")) return item;
    }
  };

  const menuItem = findPath(menuItems);
  if (!menuItem) return true;

  return !menuItem.role || menuItem.role.includes(userRole);
};



export const portionSizeUnitOptions = [
  { label: "Piece (pcs)", value: "pcs" },
  { label: "Plate (plt)", value: "plt" },
  { label: "Bowl (bowl)", value: "bowl" },
  { label: "Ounce (oz)", value: "oz" },
  { label: "Fluid Ounce (fl oz)", value: "fl_oz" },
];

export const portionTypeOptions = [
  // { label: "Regular", value: "Regular" },
  // { label: "Small", value: "Small" },
  // { label: "Medium", value: "Medium" },
  // { label: "Large", value: "Large" },
  { label: "Classic", value: "Classic" },
  // { label: "Deluxe", value: "Deluxe" },
  { label: "Feast", value: "Feast" },
];

export type MealType = "LUNCH" | "DINNER";
export type MealTypeFilter = MealType | "BOTH";

export const mealTypeOptions: { label: string; value: MealType }[] = [
  { label: "Lunch", value: "LUNCH" },
  { label: "Dinner", value: "DINNER" },
];

export const mealTypeFilterOptions: { label: string; value: MealTypeFilter }[] = [
  ...mealTypeOptions,
  { label: "Both", value: "BOTH" },
];

export const getMealLabel = (mealType: MealType) => (mealType === "LUNCH" ? "Lunch" : "Dinner");

export interface ImageObject {
  url: string;
  actualFileName: string;
  isCover: boolean;
  type?: "Image" | "Video";
}

export const normalizeImages = (images?: (string | Partial<ImageObject>)[]): ImageObject[] => {
  if (!images || !Array.isArray(images)) return [];
  return images.map((img) => {
    if (typeof img === "string") {
      const isVideo = img.toLowerCase().endsWith(".mp4") || img.toLowerCase().endsWith(".webm");
      return {
        url: img,
        actualFileName: img.split("/").pop() || "",
        isCover: false,
        type: isVideo ? "Video" : "Image",
      };
    }
    const isVideo = img.url?.toLowerCase().endsWith(".mp4") || img.url?.toLowerCase().endsWith(".webm") || img.type === "Video";
    return {
      url: img.url || "",
      actualFileName: img.actualFileName || img.url?.split("/").pop() || "",
      isCover: !!img.isCover,
      type: isVideo ? "Video" : "Image",
    };
  });
};

export const redirectScreenOptions = [
  { label: "Wallet Page", value: "WALLET" },
  { label: "Card Page", value: "CARD" },
];
export const redirectAppOptions = [
  { label: "Mealy App", value: "MEALY" },
  { label: "The Food Exchange Web", value: "TFE" },
];

export const nutritionFields = [
  { label: "Calories", field: "calories", unit: "kcal" },
  { label: "Protein", field: "protein", unit: "oz" },
  { label: "Carbs", field: "carbs", unit: "oz" },
  { label: "Fats", field: "fats", unit: "oz" },
  { label: "Fiber", field: "fiber", unit: "oz" },
];

export const getCalendarHeaderLabel = (
  activeTab: number,
  currentDate: Date,
  calendarDays: { date: Date; key: string; isCurrentMonth: boolean }[]
): string => {
  const short = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  const long = (d: Date) => d.toLocaleDateString("en-US", { month: "long" });

  if (activeTab === 0) {
    return currentDate.toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  }

  if (calendarDays.length === 0) return "";
  const first = calendarDays[0].date;
  const last = calendarDays[calendarDays.length - 1].date;

  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();

  if (sameMonth) {
    return `${long(first)} ${first.getDate()} \u2013 ${last.getDate()}, ${first.getFullYear()}`;
  }
  if (sameYear) {
    return `${short(first)} ${first.getDate()} \u2013 ${short(last)} ${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${short(first)} ${first.getDate()}, ${first.getFullYear()} \u2013 ${short(last)} ${last.getDate()}, ${last.getFullYear()}`;
};


const colorMap = [
  { color: "custom.lightGray", statuses: ["YET_TO_ORDER", "NORMAL"] },
  { color: "primary.main", statuses: ["CREATED", "PICKUP"] },
  { color: "info.main", statuses: ["IN_TRANSIT", "REFUNDED", "REPLACEMENT_ACCEPTED", "ACCEPTED"] },
  { color: "success.dark", statuses: ["READY_TO_DELIVER"] },
  { color: "success.main", statuses: ["ACTIVE", "APPROVED", "CLOSED", "CURRENT", "DELIVERED", "DELIVERY", "ONLINE", "RESOLVED", "YES"] },
  { color: "warning.light", statuses: ["PREPARING", "WAITING", "DELIVERY_ENDING_SOON"] },
  { color: "warning.main", statuses: ["CONFIRMED", "PENDING", "PENDING_USER", "REOPEN", "SCHEDULED", "UPCOMING", "NO", "LUNCH", "REPLACEMENT_REQUESTED", "FIRST_ORDER"] },
  { color: "error.dark", statuses: ["REJECTED"] },
  { color: "error.main", statuses: ["CANCELLED", "COMPLETED", "EXPIRED", "INACTIVE", "LIMIT_REACHED", "OFFLINE", "OPEN"] },
  { color: "success.dinner", statuses: ["DINNER"] },
];

export const ORDER_STATUS_COLOR: Record<string, string> = Object.fromEntries(
  colorMap.flatMap(({ color, statuses }) => statuses.map(s => [s, color]))
);

export const capitalizeFirstLetter = (str: string | boolean): string => {
  if (typeof str === "boolean") {
    str = str ? "ACTIVE" : "INACTIVE";
  }
  if (!str) return "-";
  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\b(Am|Pm)\b/g, (match) => match.toUpperCase());
};

export const getStatusColor = (status: string | boolean, theme: Theme): string => {
  if (typeof status === "boolean") {
    status = status ? "ACTIVE" : "INACTIVE";
  }
  const colorPath = ORDER_STATUS_COLOR[status] || "text.secondary";
  const parts = colorPath.split(".");
  let color: unknown = theme.palette;
  for (const part of parts) {
    color = (color as Record<string, unknown>)?.[part];
  }
  return typeof color === "string" ? color : colorPath;
};

// ─── Mail Template Schedule ───────────────────────────────────────────────────

export type ScheduleType = "ONE_TIME" | "DAILY" | "WEEKLY" | "MONTHLY" | "CUSTOM";
export type CustomIntervalType = "DAYS" | "WEEKS" | "MONTHS";

export const FREQUENCY_OPTIONS: { label: string; value: ScheduleType }[] = [
  { label: "One Time", value: "ONE_TIME" },
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "Custom", value: "CUSTOM" },
];

export const WEEKDAY_OPTIONS: { label: string; value: number }[] = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 0 },
];

export const INTERVAL_TYPE_OPTIONS: { label: string; value: CustomIntervalType }[] = [
  { label: "Days", value: "DAYS" },
  { label: "Week", value: "WEEKS" },
  { label: "Month", value: "MONTHS" },
];

export const CUSTOM_INTERVAL_MAX: Record<CustomIntervalType, number> = {
  DAYS: 6,
  WEEKS: 4,
  MONTHS: 12,
};

export const getCustomIntervalMax = (type: CustomIntervalType): number =>
  CUSTOM_INTERVAL_MAX[type];

export const isValidCustomIntervalValue = (value: string, type: CustomIntervalType): boolean => {
  const parsed = parseInt(value, 10);
  const max = getCustomIntervalMax(type);
  return value.trim() !== "" && !Number.isNaN(parsed) && parsed >= 1 && parsed <= max;
};

export const getMinCustomEndDate = (
  startDate: Dayjs,
  intervalType: CustomIntervalType,
  intervalValue: number
): Dayjs => {
  switch (intervalType) {
    case "DAYS":
      return startDate.add(intervalValue + 1, "day");
    case "WEEKS":
      return startDate.add(intervalValue * 7 + 1, "day");
    case "MONTHS":
      return startDate.add(intervalValue, "month").add(1, "day");
    default:
      return startDate;
  }
};

export const isValidMonthlyDayValue = (value: string): boolean => {
  const parsed = parseInt(value, 10);
  return value.trim() !== "" && !Number.isNaN(parsed) && parsed >= 1 && parsed <= 31;
};

export type MailTemplateType = "PROMOTIONAL" | "MARKETING" | "SUBSCRIPTION" | "TRANSACTION";
export type RecipientRole = "NEW_USERS" | "EXISTING_USERS" | "AGENCY" | "KITCHEN";
export type PushRecipientRole = "NEW_USERS" | "EXISTING_USERS" | "USER" | "DRIVER";
export type PushTemplateStatus = "pending" | "sent" | "failed";
export type MailVariableType = "text" | "textarea" | "url" | "email" | "number" | "date";

export const MAIL_TEMPLATE_TYPE_OPTIONS: { label: string; value: MailTemplateType }[] = [
  { label: "Promotional", value: "PROMOTIONAL" },
  { label: "Marketing", value: "MARKETING" },
  { label: "Subscription", value: "SUBSCRIPTION" },
  { label: "Transaction", value: "TRANSACTION" },
];

export const RECIPIENT_ROLE_OPTIONS: { label: string; value: RecipientRole }[] = [
  { label: "New Users", value: "NEW_USERS" },
  { label: "Existing Users", value: "EXISTING_USERS" },
  { label: "Agency", value: "AGENCY" },
  { label: "Kitchen", value: "KITCHEN" },
];

export const PUSH_RECIPIENT_ROLE_OPTIONS: { label: string; value: PushRecipientRole }[] = [
  { label: "New Users", value: "NEW_USERS" },
  { label: "Existing Users", value: "EXISTING_USERS" },
  { label: "Driver", value: "DRIVER" },
];

export interface PushNotificationRouteOption {
  label: string;
  value: string;
  role: PushRecipientRole[];
}

export const PUSH_NOTIFICATION_ROUTE_OPTIONS: PushNotificationRouteOption[] = [
  { label: "Home Screen", value: "homeScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER", "DRIVER"] },
  // { label: "Track Order Screen", value: "trackOrderScreen", role: ["NEW_USERS", "EXISTING_USERS","USER"] },
  { label: "My Wallet Screen", value: "myWalletScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Cart Screen", value: "cartScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Manage Cards Screen", value: "manageCardsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  // { label: "Offer Details Screen", value: "offerDetailsScreen", role: ["NEW_USERS", "EXISTING_USERS","USER"] },
  { label: "Coupons Screen", value: "couponsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Orders Screen", value: "ordersScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Notifications Screen", value: "notificationsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Get All Notifications", value: "getAllNotifications", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Main Dishes Screen", value: "mainDishesScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Box Selection Screen", value: "boxSelectionScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Subscriptions Screen", value: "subscriptionsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Profile Screen", value: "profileScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Settings Screen", value: "settingsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Refer Friends Screen", value: "referFriendsScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Address Book Screen", value: "addressBookScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
  { label: "Coin Transactions History Screen", value: "coinTransactionsHistoryScreen", role: ["NEW_USERS", "EXISTING_USERS", "USER"] },
];

export const PUSH_TEMPLATE_STATUS_OPTIONS: { label: string; value: PushTemplateStatus }[] = [
  { label: "Pending", value: "pending" },
  { label: "Sent", value: "sent" },
  { label: "Failed", value: "failed" },
];

export const MAIL_VARIABLE_TYPE_OPTIONS: { label: string; value: MailVariableType }[] = [
  { label: "Text Field", value: "text" },
  { label: "Text Area", value: "textarea" },
  { label: "URL", value: "url" },
  { label: "Email", value: "email" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
];

export const getMailTemplateTypeLabel = (type?: string) =>
  MAIL_TEMPLATE_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || type || "-";

export const getPushRecipientRoleLabel = (role?: string) => {
  if (role === "USER") return "User";
  return PUSH_RECIPIENT_ROLE_OPTIONS.find((opt) => opt.value === role)?.label || role || "-";
};

export const getPushTemplateStatusLabel = (status?: string) =>
  PUSH_TEMPLATE_STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status || "-";

export const VACATION_TEMPLATE_VARIABLES = [
  "firstName",
  "couponCode",
  "startDate",
  "endDate",
  "resumeDate",
  "daysRemaining",
  "daysOnVacation",
];

export const VACATION_PUSH_ROUTE_OPTIONS = [
  { label: "Vacation Mode", value: "vacationMode" },
  { label: "Home Screen", value: "homeScreen" },
  { label: "Coupons Screen", value: "couponsScreen" },
  { label: "Cart Screen", value: "cartScreen" },
  { label: "Orders Screen", value: "ordersScreen" },
];

export const VACATION_STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Scheduled", value: "SCHEDULED" },
];

// --------------------------- NOTIFICATION MANAGER ---------------------------
export const NOTIFICATION_CATEGORY_OPTIONS = [
  { label: "Order", value: "ORDER" },
  { label: "Payment", value: "PAYMENT" },
  { label: "Menu", value: "MENU" },
  { label: "Delivery", value: "DELIVERY" },
  { label: "Driver", value: "DRIVER" },
  { label: "Agency", value: "AGENCY" },
  { label: "Kitchen", value: "KITCHEN" },
  { label: "Agency Kitchen", value: "AGENCY_KITCHEN" },
  { label: "Marketing", value: "MARKETING" },
  { label: "Loyalty", value: "LOYALTY" },
  { label: "Auth", value: "AUTH" },
];

export const NOTIFICATION_RECIPIENT_TYPE_OPTIONS = [
  { label: "User", value: "USER" },
  { label: "Driver", value: "DRIVER" },
  { label: "Agency", value: "AGENCY" },
  { label: "Kitchen", value: "KITCHEN" },
  { label: "Agency Kitchen", value: "AGENCY_KITCHEN" },
  { label: "Admin", value: "ADMIN" },
];

export const NOTIFICATION_CHANNEL_OPTIONS: { label: string; value: "PUSH" | "EMAIL" }[] = [
  { label: "Push", value: "PUSH" },
  { label: "Email", value: "EMAIL" },
];

export const NOTIFICATION_MEAL_TYPE_OPTIONS = [
  { label: "Lunch", value: "LUNCH" },
  { label: "Dinner", value: "DINNER" },
  { label: "Both", value: "BOTH" },
];

export const NOTIFICATION_EMAIL_PLATFORM_OPTIONS = [
  { label: "Mealy", value: "MEALY" },
  { label: "The Food Exchange", value: "THE_FOOD_EXCHANGE" },
];

export const NOTIFICATION_TRIGGER_TYPE_OPTIONS = [
  { label: "Fixed Time", value: "FIXED_TIME" },
  { label: "Event", value: "EVENT" },
  { label: "Fixed Cron", value: "FIXED_CRON" },
];

export const NOTIFICATION_EVENT_NAME_OPTIONS = [
  { label: "Order Paid", value: "ORDER_PAID" },
  { label: "Payment Failed", value: "PAYMENT_FAILED" },
  { label: "Low Balance", value: "LOW_BALANCE" },
  { label: "Order Cancelled By User", value: "ORDER_CANCELLED_BY_USER" },
  { label: "Order Cancelled By Kitchen", value: "ORDER_CANCELLED_BY_KITCHEN" },
  { label: "Order Delivered", value: "ORDER_DELIVERED" },
  { label: "No Matching Menu", value: "NO_MATCHING_MENU" },
  { label: "New Campaign Created", value: "NEW_CAMPAIGN_CREATED" },
  { label: "Campaign Created For Existing", value: "CAMPAIGN_CREATED_FOR_EXISTING" },
];

export const NOTIFICATION_PUSH_TAB_OPTIONS = [
  { label: "Offers", value: "offers", route: "couponsScreen" },
  { label: "Coin History", value: "coinHistory", route: "couponsScreen" },
];
